import { freezeGiosgCart } from './utils/giosgUtils';
import type { SupportCase } from '../generated/api/models.js';

/**
 * Generic types & functions related to Google analytics
 */

export enum AnalyticsItemCategory {
  // Used in /netin-saatavuus (although it probabaly shouldn't)
  FIXED_BROADBAND = 'FIXED_BROADBAND',
  // Used in /yritysliittymat (although it shouldn't, it should match NOE).
  SALES_PRODUCT = 'Sales Product',
  YRITYS_LIITTYMA = 'Yritysliittymä',
  YRITYS_DATA_LIITTYMA = 'Yritysdata',
  LAITE_NETTI = 'Laitenetti',
  LIIKKUVA_WIFI = 'Liikkuva Wifi',
  // Fallback value if there is some unexpected error
  UNKNOWN_CATEGORY = 'Unknown category',
}

export const CURRENCY_CODE_EUR = 'EUR';

export enum AuthEventTypeV4 {
  LOGIN = 'login',
  LOGIN_FAILED = 'login_failed',
  USER_AUTH = 'user_auth',
}

export interface AuthEventV4 {
  event: AuthEventTypeV4;
  login_service?: string;
  login_method?: string;
  login_status?: string;
  amr_values?: string[];
  sso_user_id?: string;
  eid_user_id?: string;
  failure_reason?: string;
  error?: string;
  error_description?: string;
  status_code?: number;
  error_category?: string;
}

// V4 item
export type EcommerceItem = {
  affiliation?: string;
  coupon?: string;
  index?: number;
  item_brand?: string;
  item_bundle_id?: string;
  item_category: string;
  item_id: string;
  item_list_name?: string;
  item_name: string;
  item_variant?: string;
  // Price of _single_ item
  price?: number;
  quantity?: number;
};

// Use for Google Analytics v4 events
export enum EcommerceEventTypeV4 {
  ADD_PAYMENT_INFO = 'add_payment_info',
  ADD_SHIPPING_INFO = 'add_shipping_info',
  ADD_TO_CART = 'add_to_cart',
  BEGIN_CHECKOUT = 'begin_checkout',
  PURCHASE = 'purchase',
  REMOVE_ADDITIONAL_SERVICES = 'remove_additional_services',
  REMOVE_FROM_CART = 'remove_from_cart',
  SELECT_ITEM = 'select_item',
  VIEW_CART = 'view_cart',
  VIEW_ITEM = 'view_item',
  VIEW_ITEM_LIST = 'view_item_list',
}

export type EcommerceBeginCheckoutEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.BEGIN_CHECKOUT;
};

export type EcommerceAddShippingInfoEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
    // We don't send shipping_tier here because we want to send this event when
    //  the user _enters_ the shipping info step.
  };
  event: EcommerceEventTypeV4.ADD_SHIPPING_INFO;
};

export type EcommerceAddPaymentInfoEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
    // We don't send payment_type here because we want to send this event when
    //  the user _enters_ the payment info step.
  };
  event: EcommerceEventTypeV4.ADD_PAYMENT_INFO;
};

export type EcommerceAddToCartEventV4 = {
  ecommerce: {
    currency: 'EUR';
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.ADD_TO_CART;
};

export type EcommerceRemoveFromCartEventV4 = {
  ecommerce: {
    currency: 'EUR';
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.REMOVE_FROM_CART;
};

export type EcommercePurchaseEventV4 = {
  ecommerce: {
    coupon?: string;
    currency: 'EUR';
    items: EcommerceItem[];
    shipping?: number;
    tax: number;
    transaction_id: string;
    value: number;
  };
  event: EcommerceEventTypeV4.PURCHASE;
  // This is _not_ part of Google's purchase event (i.e. it's our custom stuff),
  //  so it's not written inside ecommerce.
  payment_type: string;
};

export type EcommerceRemoveAdditionalServicesEventV4 = {
  ecommerce: {
    currency: 'EUR';
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.REMOVE_ADDITIONAL_SERVICES;
};

export type EcommerceViewItemEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.VIEW_ITEM;
};

export type EcommerceViewItemListEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.VIEW_ITEM_LIST;
};

export type EcommerceSelectItemEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.SELECT_ITEM;
};

export type EcommerceViewCartEventV4 = {
  ecommerce: {
    items: EcommerceItem[];
  };
  event: EcommerceEventTypeV4.VIEW_CART;
};

type EcommerceEventV4 =
  | EcommerceBeginCheckoutEventV4
  | EcommerceAddShippingInfoEventV4
  | EcommerceAddPaymentInfoEventV4
  | EcommerceAddToCartEventV4
  | EcommerceRemoveFromCartEventV4
  | EcommercePurchaseEventV4
  | EcommerceRemoveAdditionalServicesEventV4
  | EcommerceViewItemEventV4
  | EcommerceViewItemListEventV4
  | EcommerceSelectItemEventV4
  | EcommerceViewCartEventV4;

export type CheckoutEcommerceEvent =
  | EcommerceBeginCheckoutEventV4
  | EcommerceAddShippingInfoEventV4
  | EcommerceAddPaymentInfoEventV4
  | EcommerceAddToCartEventV4
  | EcommerceRemoveFromCartEventV4
  | EcommerceViewCartEventV4;

export type EcommerceCheckoutStepEvent =
  | EcommerceEventTypeV4.BEGIN_CHECKOUT
  | EcommerceEventTypeV4.ADD_SHIPPING_INFO
  | EcommerceEventTypeV4.ADD_PAYMENT_INFO
  | EcommerceEventTypeV4.ADD_TO_CART
  | EcommerceEventTypeV4.REMOVE_FROM_CART;

const isEcommerceEventV4 = (
  event: EcommerceEventV4 | AuthEventV4 | Record<string, unknown>
): event is EcommerceEventV4 => 'ecommerce' in event && event.ecommerce != null;

declare global {
  // Resides in typescript/lib/lib.dom.d.ts, but can't be imported
  interface Window {
    dataLayer?: object[];
  }
}

export const pushToDataLayer = (obj: EcommerceEventV4 | AuthEventV4 | Record<string, unknown>) => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  // See https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#clear_the_ecommerce_object
  const eCommerceEventV4 = isEcommerceEventV4(obj);

  if (eCommerceEventV4) {
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object
  }
  window.dataLayer.push(obj);

  if (eCommerceEventV4 && obj.event === EcommerceEventTypeV4.PURCHASE) {
    freezeGiosgCart();
  }
};

const createReduxAnalyticsEvent = (eventAction: string) => {
  return {
    event: 'online-ui',
    eventCategory: 'redux',
    eventAction: eventAction,
    eventNonInteraction: 0,
    eventLabel: '',
  };
};

export const pushGAEventToDataLayer = (eventAction: string) => {
  pushToDataLayer(createReduxAnalyticsEvent(eventAction));
};

export const pushSupportCaseGAEventToDataLayer = (
  eventName: string,
  isAuthenticated: boolean,
  feature: SupportCase.FeatureEnum | string
) => {
  const event = {
    event: eventName,
    form_type: 'Support Case',
    support_case_category: feature,
    support_case_type: `${isAuthenticated ? 'authenticated' : 'anonymous'}`,
  };
  pushToDataLayer(event);
};

export const pushGlobalSearchGAEventToDataLayer = (
  eventName: string,
  multiBiz: boolean,
  selectedFilters: string[],
  resultCategories: string[],
  searchResultsTotal?: number
) => {
  pushToDataLayer({
    event: eventName,
    include_all_companies: `${multiBiz}`,
    search_result_category: resultCategories.length > 0 ? resultCategories.join(', ') : null,
    search_result_category_filter: selectedFilters.length > 0 ? selectedFilters.join(', ') : null, // null if none applied
    search_results: searchResultsTotal || 0,
  });
};

export type SettingsUpdateType = 'add' | 'remove' | 'modify';
export const pushSettingsUpdateEventToDataLayer = (
  settingCategory: string,
  settingUpdated: string,
  updateType: SettingsUpdateType = 'modify'
) => {
  pushToDataLayer({
    event: 'oe_settings_update',
    setting_category: settingCategory,
    updated_setting: settingUpdated,
    update_type: updateType,
  });
};

export const pushDomainRecordEventToDataLayer = (updateType: SettingsUpdateType) => {
  pushSettingsUpdateEventToDataLayer('dns', 'dns record', updateType);
};

export const pushBillingAccountModifyEventToDataLayer = () => {
  pushSettingsUpdateEventToDataLayer('billing account', 'billing account details');
};

export const pushSubscriptionModifyEventToDataLayer = (settingUpdated: string) => {
  pushSettingsUpdateEventToDataLayer('subscription', settingUpdated);
};

export const pushLicenseManagementGAEventToDataLayer = (eventName: string) => {
  const event = {
    event: eventName,
    eventCategory: 'license_management',
  };
  pushToDataLayer(event);
};

export const pushOECEmbedLoadEventToDataLayer = () => {
  pushToDataLayer({
    event: 'oec_embed_load',
  });
};

export const pushButtonClickEventToDataLayer = (description: string, url?: string) => {
  pushToDataLayer({
    event: 'button_click',
    click_element_parent: description,
    link_url: url,
  });
};

export const pushCtaClickEventToDataLayer = (clickElementParent: string, linkTitle?: string, linkUrl?: string) => {
  pushToDataLayer({
    event: 'cta_click',
    click_element_parent: clickElementParent,
    link_title: linkTitle,
    link_url: linkUrl,
  });
};

export const pushOECEmbedLinkEventToDataLayer = (url: string) => {
  pushButtonClickEventToDataLayer('edit_in_classic', url);
};

export const pushCampaignModalViewEventToDataLayer = (
  eventName: string,
  subscriptionName: string,
  commercialProductName: string
) => {
  pushToDataLayer({
    event: eventName,
    form_name: 'NOE mobile Subscription Upgrade',
    form_type: 'Sales Cloud',
    form_category: 'Service Subscription',
    previous_subscription: subscriptionName,
    new_subscription: commercialProductName,
  });
};

export const pushCampaignModalUpgradeEventToDataLayer = (
  eventName: string,
  subscriptionName: string,
  commercialProductName: string,
  originalMonthlyPrice: number,
  newMonthlyPrice: number
) => {
  pushToDataLayer({
    event: eventName,
    form_name: 'NOE mobile Subscription Upgrade',
    form_type: 'Sales Cloud',
    form_category: 'Service Subscription',
    previous_subscription: {
      item_name: subscriptionName,
      item_category: AnalyticsItemCategory.YRITYS_LIITTYMA,
      price: originalMonthlyPrice * 12,
      contract_type: 'OPEN-ENDED',
    },
    new_subscription: {
      item_name: commercialProductName,
      item_category: AnalyticsItemCategory.YRITYS_LIITTYMA,
      price: newMonthlyPrice * 12,
      contract_type: 'FIXED-TERM',
    },
  });
};

export const pushApplyVoucherToDataLayer = (voucherCode: string) => {
  pushToDataLayer({
    event: 'coupon_code_apply',
    coupon: voucherCode,
  });
};

export const pushLeadEventToDataLayer = (formType: string, anonymous: boolean) => {
  pushToDataLayer({
    event: 'online-ui',
    eventCategory: 'redux',
    eventAction: anonymous ? 'ADD_ANONYMOUS_LEAD_FULFILLED' : 'ADD_LEAD_FULFILLED',
    eventNonInteraction: 1,
    formType,
  });
};

export const pushKukaSoittaaLeadEventToDataLayer = () => {
  pushToDataLayer({
    event: 'form_submit',
    form_name: 'Kuka Soittaa -palvelu',
    form_type: 'Sales Cloud',
    form_category: 'Lead',
  });
};

export const pushRegistrationEventToDataLayer = (success: boolean) => {
  const target = success ? 'success' : 'failure';
  pushToDataLayer({
    event: 'interaction',
    eventCategory: 'Interactions',
    action: 'registration',
    target,
  });
};
